/**
 * Market Overview card – latest bias, strength, component breakdown, context, cautions.
 * Fetches once on load; state can be updated from WebSocket when BE sends market_overview.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  Skeleton,
  Wrap,
  WrapItem,
  SimpleGrid,
  Tooltip,
} from "@chakra-ui/react";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import { getMarketOverviewLatest } from "../../../../services/marketOverviewApi";
import type { MarketOverviewLatest, MarketBiasValue } from "../../../../types/marketOverview";

const COMPONENT_LABELS: Record<string, string> = {
  long_term_regime: "Long-term",
  short_term: "Short-term",
  price_vs_ma: "Price vs MA",
  bollinger: "Bollinger",
  rsi: "RSI",
  structure: "Structure",
  volatility: "Volatility",
  patterns: "Patterns",
};

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

/** Small label + value row for indicator blocks */
function IndicatorRow({
  label,
  value,
  textColor,
  secondaryTextColor,
}: {
  label: string;
  value: string | number | null | undefined;
  textColor: string;
  secondaryTextColor: string;
}) {
  if (value === undefined || value === null || value === "") return null;
  const display =
    typeof value === "number" && Number.isFinite(value)
      ? Math.abs(value) < 1e-3 ? value.toExponential(2) : value.toFixed(4).replace(/\.?0+$/, "")
      : String(value);
  return (
    <Flex justify="space-between" align="baseline" gap={2} fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif">
      <Text as="span" color={secondaryTextColor} fontWeight="medium">
        {label}
      </Text>
      <Text as="span" color={textColor} fontWeight="medium" noOfLines={1}>
        {display}
      </Text>
    </Flex>
  );
}

interface MarketOverviewCardProps {
  /** Display symbol, e.g. "XAU/USD" (slash is normalized for API) */
  instrument: string;
  /** Optional initial/updated data from WebSocket; when set, overrides fetched data */
  dataFromWebSocket?: MarketOverviewLatest | null;
}

export default function MarketOverviewCard({
  instrument,
  dataFromWebSocket = null,
}: MarketOverviewCardProps) {
  const [data, setData] = useState<MarketOverviewLatest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cardBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("#575757", "white");
  const secondaryTextColor = useColorModeValue("gray.400", "white");
  const labelColor = useColorModeValue("gray.400", "gray.400");
  const accentBg = useColorModeValue("gray.50", "gray.800");

  const fetchLatest = useCallback(async () => {
    if (!instrument?.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getMarketOverviewLatest(instrument);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load market overview");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [instrument]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  // When WebSocket provides data, use it (same shape as API)
  const displayData = dataFromWebSocket ?? data;

  const getBiasColor = (bias: MarketBiasValue) => {
    if (bias === "Bullish") return "green.500";
    if (bias === "Bearish") return "red.500";
    return "gray.500";
  };

  const getBiasBg = (bias: MarketBiasValue) => {
    if (bias === "Bullish") return "green.50";
    if (bias === "Bearish") return "red.50";
    return "gray.50";
  };

  return (
    <Box mx={4} mt={6} flexShrink={0}>
      <CardBody pt={0} px={0} pb={4}>
        {/* @ts-ignore - Chakra Card responsive props create complex union types */}
        <Card
          overflow="hidden"
          p="0"
          w="100%"
          bg={cardBg}
          borderRadius="md"
          border="1px solid"
          borderColor={borderColor}
          display="flex"
          flexDirection="column"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor={borderColor}
            flexShrink={0}
          >
            {/* @ts-ignore */}
            <Flex align="center" gap={2} flexWrap="wrap">
              <Text
                fontSize="xs"
                fontWeight="medium"
                color={labelColor}
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
              >
                Market Overview · {instrument}
              </Text>
              {displayData?.regime && (
                <Badge
                  fontSize="xs"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  colorScheme={displayData.regime === "trending" ? "blue" : displayData.regime === "range" ? "purple" : "gray"}
                  variant="subtle"
                  textTransform="capitalize"
                >
                  {displayData.regime}
                </Badge>
              )}
            </Flex>
            {displayData?.timestamp && (
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
              >
                Last updated {formatTimestamp(displayData.timestamp)}
              </Text>
            )}
          </Box>

          <Box px={4} py={4}>
            {loading && !displayData && (
              <VStack align="stretch" spacing={3}>
                <Skeleton height="32px" borderRadius="md" />
                <Skeleton height="24px" borderRadius="md" />
                <Skeleton height="80px" borderRadius="md" />
              </VStack>
            )}

            {error && !displayData && (
              <Text
                fontSize="sm"
                color={secondaryTextColor}
                fontFamily="'IBM Plex Sans', sans-serif"
              >
                {error}
              </Text>
            )}

            {displayData && (
              <VStack align="stretch" spacing={4}>
                {/* Bias + Strength + Confluence + Weighted */}
                <Flex align="center" gap={3} flexWrap="wrap">
                  <Badge
                    fontSize="sm"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    colorScheme={displayData.bias === "Bullish" ? "green" : displayData.bias === "Bearish" ? "red" : "gray"}
                    bg={getBiasBg(displayData.bias)}
                    color={getBiasColor(displayData.bias)}
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {displayData.bias}
                  </Badge>
                  {displayData.strength && (
                    <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                      {displayData.strength}
                    </Text>
                  )}
                  {typeof displayData.bias_score === "number" && (
                    <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                      Score: {displayData.bias_score > 0 ? "+" : ""}{displayData.bias_score}
                    </Text>
                  )}
                  {typeof displayData.weighted_score === "number" && (
                    <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                      Weighted: {displayData.weighted_score.toFixed(2)}
                    </Text>
                  )}
                  {displayData.confluence && (
                    <Badge
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      colorScheme={displayData.confluence.bullish ? "green" : displayData.confluence.bearish ? "red" : "gray"}
                      variant="outline"
                    >
                      Confluence: {displayData.confluence.bullish ? "Bullish" : displayData.confluence.bearish ? "Bearish" : "None"}
                    </Badge>
                  )}
                  {displayData.structure_conflict && (
                    <Tooltip label={displayData.structure_conflict} hasArrow placement="top">
                      <Badge fontSize="xs" colorScheme="yellow" variant="subtle" cursor="help">
                        Structure conflict
                      </Badge>
                    </Tooltip>
                  )}
                </Flex>

                {/* Zone conflict (caution near bias) */}
                {displayData.zone_conflict && (
                  <Alert status="warning" borderRadius="md" fontFamily="'IBM Plex Sans', sans-serif" size="sm">
                    <AlertIcon />
                    <AlertDescription>{displayData.zone_conflict}</AlertDescription>
                  </Alert>
                )}

                {/* Conflict */}
                {displayData.conflict && (
                  <Alert status="warning" borderRadius="md" fontFamily="'IBM Plex Sans', sans-serif">
                    <AlertIcon />
                    <AlertDescription>{displayData.conflict}</AlertDescription>
                  </Alert>
                )}

                {/* Market context one-liner */}
                {"market_context" in displayData && (displayData as { market_context?: string }).market_context && (
                  <Box
                    px={3}
                    py={2}
                    borderRadius="md"
                    bg={accentBg}
                    borderLeft="3px solid"
                    borderColor="#38BBD1"
                  >
                    <Text
                      fontSize="sm"
                      fontStyle="italic"
                      color={textColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      {(displayData as { market_context: string }).market_context}
                    </Text>
                  </Box>
                )}

                {/* Indicators snapshot */}
                {(displayData.rsi ||
                  displayData.bollinger ||
                  displayData.structure ||
                  displayData.volatility ||
                  displayData.price_vs_ma ||
                  displayData.long_term_regime ||
                  displayData.short_term ||
                  displayData.patterns) && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      mb={2}
                    >
                      Indicators
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                      {displayData.rsi && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            RSI
                          </Text>
                          <IndicatorRow label="Value" value={(displayData.rsi as { value?: number }).value} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Band" value={(displayData.rsi as { band?: string }).band} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          {(displayData.rsi as { summary?: string }).summary && (
                            <Text fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif" mt={1}>
                              {(displayData.rsi as { summary: string }).summary}
                            </Text>
                          )}
                        </Box>
                      )}
                      {displayData.bollinger && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Bollinger
                          </Text>
                          <IndicatorRow label="Position" value={(displayData.bollinger as { position?: string }).position} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Width" value={(displayData.bollinger as { width_pct?: number }).width_pct} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          {(displayData.bollinger as { upper?: number }).upper != null && (
                            <IndicatorRow label="Upper" value={(displayData.bollinger as { upper: number }).upper} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          )}
                          {(displayData.bollinger as { middle?: number }).middle != null && (
                            <IndicatorRow label="Middle" value={(displayData.bollinger as { middle: number }).middle} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          )}
                          {(displayData.bollinger as { lower?: number }).lower != null && (
                            <IndicatorRow label="Lower" value={(displayData.bollinger as { lower: number }).lower} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          )}
                        </Box>
                      )}
                      {displayData.structure && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Structure
                          </Text>
                          <IndicatorRow label="Label" value={(displayData.structure as { label?: string }).label} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="ADX" value={(displayData.structure as { adx?: number }).adx} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          {(displayData.structure as { summary?: string }).summary && (
                            <Text fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif" mt={1}>
                              {(displayData.structure as { summary: string }).summary}
                            </Text>
                          )}
                        </Box>
                      )}
                      {displayData.volatility && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Volatility
                          </Text>
                          <IndicatorRow label="Summary" value={(displayData.volatility as { summary?: string }).summary} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="ATR(14)" value={(displayData.volatility as { atr_14?: number }).atr_14} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          {(displayData.volatility as { volatility_squeeze?: boolean }).volatility_squeeze && (
                            <Badge size="sm" colorScheme="yellow" mt={1}>Squeeze</Badge>
                          )}
                          {(displayData.volatility as { volatility_expansion?: boolean }).volatility_expansion && (
                            <Badge size="sm" colorScheme="orange" mt={1} ml={1}>Expansion</Badge>
                          )}
                          {displayData.bb_width_24h && (
                            <Box mt={1}>
                              <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                                BB width 24h:{" "}
                                {displayData.bb_width_24h.current < (displayData.bb_width_24h.p25 ?? displayData.bb_width_24h.min)
                                  ? "Compressed"
                                  : displayData.bb_width_24h.current > (displayData.bb_width_24h.p75 ?? displayData.bb_width_24h.max)
                                    ? "Expanded"
                                    : "Normal"}
                              </Text>
                            </Box>
                          )}
                          {displayData.velocity_1h && (
                            <Box mt={1}>
                              <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                                Speed: {displayData.velocity_1h.avg_1h > 0 && displayData.velocity_1h.current > 1.5 * displayData.velocity_1h.avg_1h ? "Elevated" : "Normal"}
                              </Text>
                            </Box>
                          )}
                        </Box>
                      )}
                      {displayData.price_vs_ma && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Price vs MA
                          </Text>
                          {(displayData.price_vs_ma as { summary?: string }).summary && (
                            <Text fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif" mb={1}>
                              {(displayData.price_vs_ma as { summary: string }).summary}
                            </Text>
                          )}
                          <IndicatorRow label="% above EMA 50" value={(displayData.price_vs_ma as { pct_above_ema_50?: number }).pct_above_ema_50} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="% above SMA 200" value={(displayData.price_vs_ma as { pct_above_sma_200?: number }).pct_above_sma_200} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                        </Box>
                      )}
                      {displayData.long_term_regime && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Long-term
                          </Text>
                          <IndicatorRow label="Regime" value={(displayData.long_term_regime as { regime_type?: string }).regime_type} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Confidence" value={(displayData.long_term_regime as { confidence?: number }).confidence} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Trend strength" value={(displayData.long_term_regime as { trend_strength?: number }).trend_strength} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                        </Box>
                      )}
                      {displayData.short_term && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Short-term
                          </Text>
                          <IndicatorRow label="Phase" value={(displayData.short_term as { market_phase?: string }).market_phase} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Confidence" value={(displayData.short_term as { confidence?: number }).confidence} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Bars in push" value={(displayData.short_term as { bars_in_push?: number }).bars_in_push} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Shift" value={(displayData.short_term as { trend_shift_direction?: string }).trend_shift_direction} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                          <IndicatorRow label="Velocity (pips/s)" value={(displayData.short_term as { velocity_pips_per_second?: number }).velocity_pips_per_second} textColor={textColor} secondaryTextColor={secondaryTextColor} />
                        </Box>
                      )}
                      {displayData.patterns && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor={borderColor} gridColumn={{ base: "1", sm: "1", md: "1 / -1" }}>
                          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Patterns
                          </Text>
                          {(displayData.patterns as { pattern_count?: number }).pattern_count != null && (
                            <Text fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif" mb={1}>
                              {(displayData.patterns as { pattern_count: number }).pattern_count} active pattern(s)
                            </Text>
                          )}
                          {(displayData.patterns as { summary?: string }).summary && (
                            <Text fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif" mb={2}>
                              {(displayData.patterns as { summary: string }).summary}
                            </Text>
                          )}
                          {(displayData.patterns as { pattern_types?: string[] }).pattern_types &&
                            (displayData.patterns as { pattern_types: string[] }).pattern_types.length > 0 && (
                              <Wrap spacing={1}>
                                {(displayData.patterns as { pattern_types: string[] }).pattern_types.slice(0, 12).map((p: string, i: number) => (
                                  <WrapItem key={i}>
                                    <Badge fontSize="xs" colorScheme="gray" variant="subtle" fontFamily="'IBM Plex Sans', sans-serif">
                                      {p}
                                    </Badge>
                                  </WrapItem>
                                ))}
                                {(displayData.patterns as { pattern_types: string[] }).pattern_types.length > 12 && (
                                  <WrapItem>
                                    <Text fontSize="xs" color={secondaryTextColor}>+{(displayData.patterns as { pattern_types: string[] }).pattern_types.length - 12} more</Text>
                                  </WrapItem>
                                )}
                              </Wrap>
                            )}
                        </Box>
                      )}
                    </SimpleGrid>
                  </Box>
                )}

                {/* Component raw scores (debug/advanced) */}
                {displayData.component_raw_scores && Object.keys(displayData.component_raw_scores).length > 0 && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      mb={2}
                    >
                      Component scores
                    </Text>
                    <Wrap spacing={2}>
                      {Object.entries(displayData.component_raw_scores).map(([key, value]) => (
                        <WrapItem key={key}>
                          <Badge
                            fontSize="xs"
                            fontFamily="'IBM Plex Sans', sans-serif"
                            colorScheme={value === 1 ? "green" : value === -1 ? "red" : "gray"}
                            variant="subtle"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                          >
                            {COMPONENT_LABELS[key] ?? key}: {value}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}

                {/* Component breakdown */}
                {displayData.component_breakdown &&
                  Object.keys(displayData.component_breakdown).length > 0 && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color={secondaryTextColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        textTransform="uppercase"
                        mb={2}
                      >
                        Component breakdown
                      </Text>
                      <Wrap spacing={2}>
                        {Object.entries(displayData.component_breakdown).map(([key, value]) => (
                          <WrapItem key={key}>
                            <Badge
                              size="sm"
                              fontSize="xs"
                              fontFamily="'IBM Plex Sans', sans-serif"
                              colorScheme={value === "Bullish" ? "green" : value === "Bearish" ? "red" : "gray"}
                              variant="subtle"
                              px={2}
                              py={0.5}
                              borderRadius="md"
                            >
                              {COMPONENT_LABELS[key] ?? key}: {value}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>
                  )}

                {/* Nearest levels */}
                {(displayData.nearest_supply || displayData.nearest_demand) && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      mb={2}
                    >
                      Nearest levels
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                      {displayData.nearest_supply && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor="red.200">
                          <Text fontSize="xs" color="red.500" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Nearest supply
                          </Text>
                          <Text fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
                            {displayData.nearest_supply.price_range} ({displayData.nearest_supply.count}) {displayData.nearest_supply.levels?.join(", ")}
                          </Text>
                          {displayData.nearest_supply.mid_price != null && (
                            <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                              Mid: {displayData.nearest_supply.mid_price.toFixed(2)}
                              {displayData.nearest_supply.distance != null && ` · Distance: ${displayData.nearest_supply.distance.toFixed(2)}`}
                            </Text>
                          )}
                        </Box>
                      )}
                      {displayData.nearest_demand && (
                        <Box px={3} py={2} borderRadius="md" borderWidth="1px" borderColor="green.200">
                          <Text fontSize="xs" color="green.500" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                            Nearest demand
                          </Text>
                          <Text fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
                            {displayData.nearest_demand.price_range} ({displayData.nearest_demand.count}) {displayData.nearest_demand.levels?.join(", ")}
                          </Text>
                          {displayData.nearest_demand.mid_price != null && (
                            <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                              Mid: {displayData.nearest_demand.mid_price.toFixed(2)}
                              {displayData.nearest_demand.distance != null && ` · Distance: ${displayData.nearest_demand.distance.toFixed(2)}`}
                            </Text>
                          )}
                        </Box>
                      )}
                    </SimpleGrid>
                  </Box>
                )}

                {/* Zone clusters (supply / demand) */}
                {displayData.zone_clusters &&
                  ((displayData.zone_clusters.supply_clusters?.length ?? 0) > 0 ||
                    (displayData.zone_clusters.demand_clusters?.length ?? 0) > 0) && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color={secondaryTextColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        textTransform="uppercase"
                        mb={2}
                      >
                        Zone clusters
                      </Text>
                      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        {displayData.zone_clusters.supply_clusters && displayData.zone_clusters.supply_clusters.length > 0 && (
                          <Box>
                            <Text fontSize="xs" color="red.500" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                              Supply
                            </Text>
                            <VStack align="stretch" spacing={1}>
                              {displayData.zone_clusters.supply_clusters.map((c: { price_range: string; count: number; levels: string[] }, i: number) => (
                                <Text key={i} fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
                                  {c.price_range} ({c.count}) {c.levels?.join(", ")}
                                </Text>
                              ))}
                            </VStack>
                          </Box>
                        )}
                        {displayData.zone_clusters.demand_clusters && displayData.zone_clusters.demand_clusters.length > 0 && (
                          <Box>
                            <Text fontSize="xs" color="green.500" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mb={1}>
                              Demand
                            </Text>
                            <VStack align="stretch" spacing={1}>
                              {displayData.zone_clusters.demand_clusters.map((c: { price_range: string; count: number; levels: string[] }, i: number) => (
                                <Text key={i} fontSize="xs" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
                                  {c.price_range} ({c.count}) {c.levels?.join(", ")}
                                </Text>
                              ))}
                            </VStack>
                          </Box>
                        )}
                      </SimpleGrid>
                    </Box>
                  )}

                {/* Context */}
                {displayData.context && displayData.context.length > 0 && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      mb={2}
                    >
                      Context
                    </Text>
                    <VStack align="stretch" spacing={1}>
                      {displayData.context.map((line, i) => (
                        <Text
                          key={i}
                          fontSize="sm"
                          color={textColor}
                          fontFamily="'IBM Plex Sans', sans-serif"
                          as="span"
                          display="block"
                        >
                          · {line}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Cautions */}
                {displayData.cautions && displayData.cautions.length > 0 && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      mb={2}
                    >
                      Cautions
                    </Text>
                    <HStack flexWrap="wrap" spacing={2}>
                      {displayData.cautions.map((c, i) => (
                        <Badge
                          key={i}
                          fontSize="xs"
                          fontFamily="'IBM Plex Sans', sans-serif"
                          colorScheme="yellow"
                          variant="subtle"
                          px={2}
                          py={0.5}
                          borderRadius="md"
                        >
                          {c}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                )}
              </VStack>
            )}
          </Box>
        </Card>
      </CardBody>
    </Box>
  );
}
